/**
 * Open a citation's source document in a new tab — at the cited page for a
 * PDF. The content route is authenticated and audited server-side
 * (`document_index.document_read`), so the file is fetched with the bearer
 * token rather than linked.
 */

import { useState } from "react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { toastApiError } from "@/shared/ui/toast";
import { type Citation, pageFragment, splitContentUrl } from "./citation";
import { useDocumentViewer } from "./DocumentViewer";
import { openAuthenticatedInNewTab } from "./openAuthenticated";

export function useOpenCitation(): {
  open: (citation: Citation) => Promise<void>;
  opening: boolean;
} {
  const getToken = useAccessToken();
  const viewer = useDocumentViewer();
  const [opening, setOpening] = useState(false);

  const open = async (citation: Citation) => {
    // In the app shell the document opens in a pop-up viewer (no download).
    if (viewer) {
      viewer.openDocument({
        path: splitContentUrl(citation.content_url).path,
        fragment: pageFragment(citation),
        name: citation.display_name,
      });
      return;
    }
    setOpening(true);
    try {
      await openAuthenticatedInNewTab(
        getToken,
        splitContentUrl(citation.content_url).path,
        pageFragment(citation),
        citation.display_name,
      );
    } catch (err) {
      toastApiError("Could not open the source document", err);
    } finally {
      setOpening(false);
    }
  };

  return { open, opening };
}
